import { CareOSLoader } from '@/components/ui/careos-loader';
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldAlert } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLegalDocsVersionCheck } from "@/hooks/useLegalDocsVersionCheck";

interface LegalDoc {
  slug: string;
  version: string;
  title: string;
  content_md: string;
}

const SLUG_TITLES: Record<string, string> = {
  terms: "Termos de Uso",
  privacy: "Política de Privacidade",
};

/**
 * Blocking modal shown after login when one or more mandatory legal documents
 * have a newer effective version than the user's last acceptance.
 *
 * The user must scroll through and accept each pending document before
 * being able to continue using the app.
 */
export function LegalReacceptanceModal() {
  const { needsReacceptance, refresh } = useLegalDocsVersionCheck();
  const [docs, setDocs] = useState<LegalDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [index, setIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const viewport = scrollRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (viewport) viewport.scrollTop = 0;
  }, [index]);

  useEffect(() => {
    if (needsReacceptance.length === 0) {
      setDocs([]);
      setIndex(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data } = await supabase
        .from("legal_documents")
        .select("slug, version, title, content_md")
        .in("slug", needsReacceptance)
        .eq("locale", "pt-BR")
        .is("superseded_at", null)
        .order("effective_at", { ascending: false });

      if (cancelled) return;

      // Pick latest per slug, in the same order as needsReacceptance
      const bySlug = new Map<string, LegalDoc>();
      for (const d of (data as LegalDoc[]) ?? []) {
        if (!bySlug.has(d.slug)) bySlug.set(d.slug, d);
      }
      setDocs(needsReacceptance.map((s) => bySlug.get(s)).filter(Boolean) as LegalDoc[]);
      setIndex(0);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [needsReacceptance]);

  const open = docs.length > 0;
  const current = docs[index];

  const handleAccept = async () => {
    if (!current) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("record-acceptance", {
        body: { slugs: [current.slug], method: "forced_review" },
      });
      if (error || (data as { error?: string })?.error) {
        throw new Error((data as { error?: string })?.error ?? error?.message);
      }
      if (index < docs.length - 1) {
        setIndex(index + 1);
      } else {
        toast.success("Obrigado por aceitar a nova versão.");
        await refresh();
      }
    } catch (e) {
      toast.error(`Não foi possível registrar o aceite: ${(e as Error).message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const title = current?.title ?? SLUG_TITLES[current?.slug ?? ""] ?? "Documento legal";

  return (
    <Dialog open onOpenChange={() => { /* blocking */ }}>
      <DialogContent
        className="max-w-2xl [&>button[aria-label='Close']]:hidden [&_button.absolute.right-4.top-4]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-warning" />
            Atualizamos {title}
          </DialogTitle>
          <DialogDescription>
            Para continuar usando o CareOS, leia e aceite a versão {current?.version}.
            {docs.length > 1 && ` (${index + 1} de ${docs.length})`}
          </DialogDescription>
        </DialogHeader>

        {loading || !current ? (
          <div className="flex items-center justify-center py-12">
            <CareOSLoader size="md" />
          </div>
        ) : (
          <ScrollArea ref={scrollRef} className="h-[55vh] border rounded-md p-4">
            <article className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {current.content_md}
              </ReactMarkdown>
            </article>
          </ScrollArea>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={handleAccept} disabled={submitting || loading || !current}>
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {index < docs.length - 1 ? "Aceitar e continuar" : "Aceitar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
